import {
	Button,
	Checkbox,
	Flex,
	Form,
	Grid,
	Modal,
	Select,
	Space,
	Spin,
	Switch,
	Tag,
	Typography,
	theme,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { useCreateTaskTemplate } from "@/common/artemis-operations/task-templates";
import { useGalleryManifest } from "@/common/artemis-operations/template-gallery";
import type { EnvironmentProvider } from "@/common/artemis-sdk/graphql";
import {
	ERROR_GALLERY_BULK_ADD_DESC,
	ERROR_GALLERY_BULK_ADD_TITLE,
	ERROR_GALLERY_TEMPLATE_DESC,
	ERROR_GALLERY_TEMPLATE_TITLE,
	ERROR_GALLERY_UNTRUSTED_URL_DESC,
	ERROR_GALLERY_UNTRUSTED_URL_TITLE,
	GALLERY_BULK_ADDED_DESC,
} from "@/common/messages";
import {
	collectPlaceholderValues,
	substituteVariables,
} from "@/common/placeholders";
import {
	fetchGalleryTemplate,
	type GalleryManifestEntry,
	type GalleryTemplate,
	isAllowedGalleryUrl,
	resolveGalleryTemplateUrl,
} from "@/common/template-gallery";
import { useNotify } from "@/common/use-notify";
import { QueryGuard } from "@/components/base";
import { CardBody, CardViewer } from "@/components/card-viewer";
import {
	EnvironmentProviderBadge,
	EnvironmentProviderLabelMap,
} from "@/components/environments/environment-badges";
import { PlaceholderFormItem } from "@/components/placeholders/placeholder-input";

const PAGE_SIZE = 8;

/** A template's `cloudProvider` reuses the platform's known providers when
 * possible (for the shared badge and its label); anything else falls back to
 * the raw value as-is. */
function ProviderBadge({ cloudProvider }: { cloudProvider: string }) {
	if (cloudProvider in EnvironmentProviderLabelMap) {
		return (
			<EnvironmentProviderBadge type={cloudProvider as EnvironmentProvider} />
		);
	}
	return <Tag>{cloudProvider}</Tag>;
}

function providerLabel(cloudProvider: string): string {
	return cloudProvider in EnvironmentProviderLabelMap
		? EnvironmentProviderLabelMap[cloudProvider as EnvironmentProvider]
		: cloudProvider;
}

function fieldName(templateId: string, variableKey: string): string {
	return `${templateId}__${variableKey}`;
}

/** A fetched template's preview state: in flight, loaded, or failed. */
type PreviewEntry = GalleryTemplate | "loading" | "error";

interface TemplateGalleryBrowserProps {
	open: boolean;
	onClose: () => void;
	workspaceId: string;
	/** Pre-known environment to bind every created template to (e.g. one just created,
	 * or the one this page is for). Omitted, the customize step asks for one instead. */
	environmentId?: string;
	/** Pre-applies the provider filter, e.g. to the environment this browse is for. */
	defaultProviderFilter?: string;
	/** A `?importTemplate=<url>` value — skips browsing, jumps straight to customizing
	 * this one template. */
	autoImportUrl?: string;
	/** Called once `autoImportUrl` has been handled, so the caller can clear it. */
	onAutoImportHandled?: () => void;
}

/**
 * Full-page gallery: browse (search/filter, paginated list on the left, the
 * previewed template's details on the right), pick one or many, then a customize
 * step — a continuation of the same page — resolves freeform variables (and an
 * environment, if none was already known) before creating them all as workspace
 * task templates at once.
 */
export function TemplateGalleryBrowser({
	open,
	onClose,
	workspaceId,
	environmentId,
	defaultProviderFilter,
	autoImportUrl,
	onAutoImportHandled,
}: TemplateGalleryBrowserProps) {
	const { token } = theme.useToken();
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;
	const notify = useNotify();
	const createTaskTemplate = useCreateTaskTemplate();
	const { data: manifest, isPending, isError } = useGalleryManifest();
	const { data: environments } = useEnvironments(workspaceId);
	const [form] = Form.useForm();

	const [page, setPage] = useState<"browse" | "customize">("browse");
	const [multiMode, setMultiMode] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [previewedId, setPreviewedId] = useState<string>();
	const [previewCache, setPreviewCache] = useState<
		Record<string, PreviewEntry>
	>({});
	const [customizeTemplates, setCustomizeTemplates] = useState<
		GalleryTemplate[] | null
	>(null);
	const [customizePreviewId, setCustomizePreviewId] = useState<string>();
	const [saving, setSaving] = useState(false);
	const handledAutoImportUrl = useRef<string | undefined>(undefined);

	// The Modal's own destroyOnHidden only unmounts its children - state declared here,
	// in the wrapping component, would otherwise persist across a close/reopen. Reset it
	// on close so the next open always starts fresh.
	useEffect(() => {
		if (open) return;
		setPage("browse");
		setMultiMode(false);
		setSelected(new Set());
		setPreviewedId(undefined);
		setPreviewCache({});
		setCustomizeTemplates(null);
		setCustomizePreviewId(undefined);
		setSaving(false);
		handledAutoImportUrl.current = undefined;
		form.resetFields();
	}, [open, form]);

	const providers = useMemo(
		() => Array.from(new Set((manifest ?? []).map((t) => t.cloudProvider))),
		[manifest],
	);
	const categories = useMemo(
		() => Array.from(new Set((manifest ?? []).map((t) => t.category))),
		[manifest],
	);

	// Default the preview to the first result once the manifest loads.
	useEffect(() => {
		if (!previewedId && manifest && manifest.length > 0) {
			setPreviewedId(manifest[0].id);
		}
	}, [manifest, previewedId]);

	// Default which template's parameters show whenever the customize set changes.
	useEffect(() => {
		if (customizeTemplates && customizeTemplates.length > 0) {
			setCustomizePreviewId(customizeTemplates[0].id);
		}
	}, [customizeTemplates]);

	// Fetch + validate a template's full YAML the first time it's previewed; cached so
	// re-previewing (or continuing with it) doesn't re-fetch.
	// biome-ignore lint/correctness/useExhaustiveDependencies: previewCache read, not reacted to — see above
	useEffect(() => {
		if (!previewedId || previewCache[previewedId]) return;
		const entry = (manifest ?? []).find((t) => t.id === previewedId);
		if (!entry) return;
		setPreviewCache((prev) => ({ ...prev, [previewedId]: "loading" }));
		fetchGalleryTemplate(resolveGalleryTemplateUrl(entry.path))
			.then((t) => setPreviewCache((prev) => ({ ...prev, [previewedId]: t })))
			.catch(() =>
				setPreviewCache((prev) => ({ ...prev, [previewedId]: "error" })),
			);
	}, [previewedId, manifest]);

	const toggleSelected = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const continueIds = multiMode
		? Array.from(selected)
		: previewedId
			? [previewedId]
			: [];

	const handleContinue = async () => {
		const entries = (manifest ?? []).filter((t) => continueIds.includes(t.id));
		const results = await Promise.all(
			entries.map(async (entry) => {
				const cached = previewCache[entry.id];
				if (cached && cached !== "loading" && cached !== "error") return cached;
				try {
					return await fetchGalleryTemplate(
						resolveGalleryTemplateUrl(entry.path),
					);
				} catch {
					return null;
				}
			}),
		);
		const ok = results.filter((t): t is GalleryTemplate => t !== null);
		if (ok.length < results.length) {
			notify.error(ERROR_GALLERY_TEMPLATE_TITLE, ERROR_GALLERY_TEMPLATE_DESC);
		}
		if (ok.length === 0) return;
		setCustomizeTemplates(ok);
		setPage("customize");
	};

	const createOne = (
		template: GalleryTemplate,
		values: Record<string, string | number | boolean>,
	) =>
		new Promise<boolean>((resolve) => {
			const targetEnvironmentId =
				environmentId ??
				(values[fieldName(template.id, "environmentId")] as string);
			const varValues = collectPlaceholderValues(
				template.placeholders?.variables ?? [],
				(key) => values[fieldName(template.id, key)],
			);
			const sub = (text?: string) =>
				text !== undefined ? substituteVariables(text, varValues) : text;
			createTaskTemplate(
				{
					name: sub(template.task.name) ?? template.task.name,
					description:
						sub(template.task.description) ?? template.task.description,
					instructions: sub(template.task.instructions),
					verificationCriteria: sub(template.task.verificationCriteria),
					environmentIds: [targetEnvironmentId],
					workspaceId,
				},
				{
					onSuccess: () => resolve(true),
					onError: () => resolve(false),
				},
			);
		});

	const handleSubmitCustomize = async () => {
		if (!customizeTemplates || customizeTemplates.length === 0) return;
		let values: Record<string, string | number | boolean>;
		try {
			values = await form.validateFields();
		} catch {
			// antd already focuses/highlights the invalid field(s).
			return;
		}
		setSaving(true);
		try {
			// Sequential, not Promise.all: `createTaskTemplate` shares one useMutation
			// instance, which tracks only the most recent call's onSuccess/onError —
			// firing several mutate() calls concurrently drops every callback but the
			// last, leaving earlier callers' promises (and this button's spinner)
			// stuck forever even though the mutations themselves all succeed server-side.
			const results: boolean[] = [];
			for (const t of customizeTemplates) {
				results.push(await createOne(t, values));
			}
			const succeeded = results.filter(Boolean).length;
			if (succeeded > 0) {
				notify.success(
					`${succeeded} template${succeeded === 1 ? "" : "s"} added`,
					GALLERY_BULK_ADDED_DESC,
				);
			}
			if (succeeded < results.length) {
				notify.error(ERROR_GALLERY_BULK_ADD_TITLE, ERROR_GALLERY_BULK_ADD_DESC);
			}
			onClose();
		} finally {
			setSaving(false);
		}
	};

	// Deep link: skip browsing, fetch this one template directly, jump to customize.
	useEffect(() => {
		if (!autoImportUrl || handledAutoImportUrl.current === autoImportUrl)
			return;
		handledAutoImportUrl.current = autoImportUrl;
		if (!isAllowedGalleryUrl(autoImportUrl)) {
			notify.error(
				ERROR_GALLERY_UNTRUSTED_URL_TITLE,
				ERROR_GALLERY_UNTRUSTED_URL_DESC,
			);
			onAutoImportHandled?.();
			return;
		}
		fetchGalleryTemplate(autoImportUrl)
			.then((t) => {
				setCustomizeTemplates([t]);
				setPage("customize");
			})
			.catch(() =>
				notify.error(ERROR_GALLERY_TEMPLATE_TITLE, ERROR_GALLERY_TEMPLATE_DESC),
			)
			.finally(() => onAutoImportHandled?.());
	}, [autoImportUrl, notify, onAutoImportHandled]);

	const renderPreview = () => {
		if (!previewedId)
			return (
				<Typography.Text type="secondary">
					Select a template to see its details.
				</Typography.Text>
			);
		const cached = previewCache[previewedId];
		if (!cached || cached === "loading")
			return (
				<Flex justify="center" style={{ padding: 24 }}>
					<Spin />
				</Flex>
			);
		if (cached === "error")
			return (
				<Typography.Text type="danger">
					This template could not be loaded.
				</Typography.Text>
			);
		const t = cached;
		const hasPlaceholders =
			t.placeholders?.environment ||
			t.placeholders?.agent ||
			(t.placeholders?.variables?.length ?? 0) > 0;
		return (
			<Flex vertical gap={12}>
				<Typography.Title level={3} style={{ marginTop: 0, marginBottom: 0 }}>
					{t.name}
				</Typography.Title>
				<Space>
					<Tag>{t.category}</Tag>
					<ProviderBadge cloudProvider={t.cloudProvider} />
				</Space>
				<Typography.Paragraph>{t.description}</Typography.Paragraph>
				<Typography.Title level={5} style={{ marginBottom: 0 }}>
					What this does
				</Typography.Title>
				<Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
					{t.task.instructions}
				</Typography.Paragraph>
				{hasPlaceholders && (
					<>
						<Typography.Title level={5} style={{ marginBottom: 0 }}>
							You'll be asked for
						</Typography.Title>
						<ul style={{ marginTop: 0 }}>
							{t.placeholders?.environment && (
								<li>{t.placeholders.environment.label}</li>
							)}
							{t.placeholders?.agent && <li>{t.placeholders.agent.label}</li>}
							{(t.placeholders?.variables ?? []).map((v) => (
								<li key={v.key}>{v.label}</li>
							))}
						</ul>
					</>
				)}
			</Flex>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={page === "browse" ? "Browse gallery" : "Customize templates"}
			width="100vw"
			style={{ top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 }}
			maskClosable={false}
			destroyOnHidden
			styles={{
				container: {
					height: "100vh",
					borderRadius: 0,
					display: "flex",
					flexDirection: "column",
				},
				body: { flex: 1, minHeight: 0, paddingTop: 12, display: "flex" },
			}}
			footer={
				page === "browse" ? (
					<Space>
						<Button onClick={onClose}>Cancel</Button>
						<Button
							type="primary"
							disabled={continueIds.length === 0}
							onClick={handleContinue}
						>
							Continue
							{continueIds.length > 0 ? ` with ${continueIds.length}` : ""}
						</Button>
					</Space>
				) : (
					<Space>
						<Button onClick={() => setPage("browse")}>Back</Button>
						<Button
							type="primary"
							loading={saving}
							onClick={handleSubmitCustomize}
						>
							Add {customizeTemplates?.length ?? 0} template
							{customizeTemplates?.length === 1 ? "" : "s"}
						</Button>
					</Space>
				)
			}
		>
			{page === "browse" ? (
				<Flex
					vertical={isMobile}
					gap={24}
					style={{ flex: 1, minHeight: 0, width: "100%" }}
				>
					<Flex
						vertical
						gap={12}
						style={{
							flex: isMobile ? 1 : "0 0 380px",
							minHeight: 0,
						}}
					>
						<div
							className="scroll-no-bar"
							style={{ flex: 1, minHeight: 0, paddingInline: 18 }}
						>
							<QueryGuard isPending={isPending} isError={isError}>
								<CardViewer
									items={manifest ?? []}
									getKey={(t) => t.id}
									onOpen={(t) => setPreviewedId(t.id)}
									isSelected={(t) => previewedId === t.id}
									searchText={(t) => `${t.name} ${t.description}`}
									searchPlaceholder="Search templates"
									minCardWidth={340}
									pageSize={PAGE_SIZE}
									initialFilterValues={
										defaultProviderFilter
											? { cloudProvider: defaultProviderFilter }
											: undefined
									}
									extra={
										<Space>
											<Typography.Text>Select multiple</Typography.Text>
											<Switch
												size="small"
												checked={multiMode}
												onChange={(checked) => {
													setMultiMode(checked);
													if (!checked) setSelected(new Set());
												}}
											/>
										</Space>
									}
									filters={[
										...(providers.length > 1
											? [
													{
														key: "cloudProvider",
														allLabel: "All providers",
														options: providers.map((p) => ({
															value: p,
															label: providerLabel(p),
														})),
														predicate: (
															t: GalleryManifestEntry,
															value: string,
														) => t.cloudProvider === value,
													},
												]
											: []),
										...(categories.length > 1
											? [
													{
														key: "category",
														allLabel: "All categories",
														options: categories.map((c) => ({
															value: c,
															label: c,
														})),
														predicate: (
															t: GalleryManifestEntry,
															value: string,
														) => t.category === value,
													},
												]
											: []),
									]}
									renderCard={(t) => (
										<CardBody
											title={t.name}
											leading={
												multiMode && (
													<Checkbox
														checked={selected.has(t.id)}
														onClick={(e) => {
															e.stopPropagation();
															toggleSelected(t.id);
														}}
													/>
												)
											}
											description={t.description}
											extra={
												<Space size={4}>
													<Tag>{t.category}</Tag>
													<ProviderBadge cloudProvider={t.cloudProvider} />
												</Space>
											}
										/>
									)}
								/>
							</QueryGuard>
						</div>
					</Flex>
					<div
						className="scroll-no-bar"
						style={{
							flex: 1,
							minWidth: 0,
							minHeight: 0,
							borderLeft: isMobile
								? undefined
								: `1px solid ${token.colorBorder}`,
							paddingLeft: isMobile ? 0 : 24,
							paddingRight: 18,
						}}
					>
						{renderPreview()}
					</div>
				</Flex>
			) : (
				<Form
					form={form}
					layout="vertical"
					style={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}
				>
					<Flex
						vertical={isMobile}
						gap={24}
						style={{ flex: 1, minHeight: 0, width: "100%" }}
					>
						<Flex
							vertical
							gap={8}
							style={{
								flex: isMobile ? 1 : "0 0 320px",
								minHeight: 0,
							}}
						>
							<div
								className="scroll-no-bar"
								style={{ flex: 1, minHeight: 0, paddingInline: 18 }}
							>
								<CardViewer
									items={customizeTemplates ?? []}
									getKey={(t) => t.id}
									onOpen={(t) => setCustomizePreviewId(t.id)}
									isSelected={(t) => customizePreviewId === t.id}
									minCardWidth={280}
									renderCard={(t) => (
										<CardBody title={t.name} description={t.description} />
									)}
								/>
							</div>
						</Flex>
						<div
							className="scroll-no-bar"
							style={{
								flex: 1,
								minWidth: 0,
								borderLeft: isMobile
									? undefined
									: `1px solid ${token.colorBorder}`,
								paddingLeft: isMobile ? 0 : 24,
								paddingRight: 18,
							}}
						>
							{(customizeTemplates ?? []).map((template) => (
								<div
									key={template.id}
									style={{
										display:
											template.id === customizePreviewId ? undefined : "none",
									}}
								>
									<Typography.Title level={4} style={{ marginTop: 0 }}>
										{template.name}
									</Typography.Title>
									{!environmentId && (
										<Form.Item
											name={fieldName(template.id, "environmentId")}
											label="Environment"
											rules={[
												{ required: true, message: "Select an environment" },
											]}
											style={{ maxWidth: 380 }}
										>
											<Select
												placeholder="Select an environment"
												options={(environments ?? [])
													.filter((e) => e != null)
													.map((e) => ({ value: e.id, label: e.name }))}
											/>
										</Form.Item>
									)}
									{(template.placeholders?.variables ?? []).length === 0
										? environmentId && (
												<Typography.Text type="secondary">
													No additional settings needed.
												</Typography.Text>
											)
										: (template.placeholders?.variables ?? []).map(
												(variable) => (
													<PlaceholderFormItem
														key={variable.key}
														field={variable}
														name={fieldName(template.id, variable.key)}
														style={{ marginBottom: 8, maxWidth: 480 }}
													/>
												),
											)}
								</div>
							))}
						</div>
					</Flex>
				</Form>
			)}
		</Modal>
	);
}
